import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, differenceInDays, addYears } from 'date-fns';
import { fmtPatron } from '../utils/dates';
import { Download, XCircle, Clock, AlertTriangle, RefreshCw } from 'lucide-react';

const MOTIVOS_RECUPERABLES = [
  'Cambio de corredor',
  'Precio',
  'Contratación directa con aseguradora',
  'Falta de seguimiento',
  'Otro',
];

export default function ReportsPage() {
  const { policies, clients, updatePolicy, showToast } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState('expired');
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  const getClient = id => clients.find(c => c.id === id) || {};

  // ── Datos por pestaña ──────────────────────────────────────
  const expired = policies
    .filter(p => p.status === 'expired')
    .sort((a, b) => (a.endDate > b.endDate ? 1 : -1));

  const cancelled = policies.filter(p => p.status === 'cancelled');

  // Canceladas recuperables: motivo NO es "Venta del vehículo" ni "Siniestro total"
  const MOTIVOS_NO_RECUPERABLES = ['Venta del vehículo', 'Siniestro total', 'Fallecimiento'];
  const cancelledRecoverable = cancelled.filter(p => {
    if (!p.cancelReason) return true;
    return !MOTIVOS_NO_RECUPERABLES.some(m => p.cancelReason?.toLowerCase().includes(m.toLowerCase()));
  });

  // Alerta comercial: recuperables cuyo aniversario de renovación cae en los próximos 30 días
  const commercialAlerts = cancelledRecoverable.filter(p => {
    if (!p.cancelDate && !p.endDate) return false;
    const baseDate = p.endDate || p.cancelDate;
    try {
      const anniversary = addYears(parseISO(baseDate), 1);
      const daysToAnniversary = differenceInDays(anniversary, today);
      return daysToAnniversary >= 0 && daysToAnniversary <= 30;
    } catch { return false; }
  });

  const expiring = policies.filter(p => {
    if (p.status !== 'active' && p.status !== 'endorsed') return false;
    if (!p.endDate) return false;
    const days = differenceInDays(parseISO(p.endDate), today);
    return days >= 0 && days <= 60;
  }).sort((a, b) => parseISO(a.endDate) - parseISO(b.endDate));

  // ── CSV export ─────────────────────────────────────────────
  const downloadCSV = (headers, rows, filename) => {
    const escape = v => { const s = String(v ?? '').replace(/"/g,'""'); return s.includes(',') || s.includes('"') ? `"${s}"` : s; };
    const lines = [headers.join(','), ...rows.map(r => r.map(escape).join(','))];
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportExpired = () => {
    downloadCSV(
      ['Cliente','Teléfono','Email','N° Póliza','Compañía','Ramo','Prima','Venció'],
      expired.map(p => { const c = getClient(p.clientId); return [c.name, c.phone, c.email, p.number, p.company, p.type, p.premium, p.endDate]; }),
      `NEBB_vencidas_sin_renovar_${todayStr}.csv`
    );
  };

  const exportCancelled = () => {
    downloadCSV(
      ['Cliente','Teléfono','Email','N° Póliza','Compañía','Prima','Cancelada','Motivo'],
      cancelled.map(p => { const c = getClient(p.clientId); return [c.name, c.phone, c.email, p.number, p.company, p.premium, p.cancelDate, p.cancelReason]; }),
      `NEBB_cancelaciones_${todayStr}.csv`
    );
  };

  const exportAlerts = () => {
    downloadCSV(
      ['Cliente','Teléfono','Email','N° Póliza','Compañía','Motivo cancelación','Aniversario renovación'],
      commercialAlerts.map(p => {
        const c = getClient(p.clientId);
        const ann = addYears(parseISO(p.endDate || p.cancelDate), 1);
        return [c.name, c.phone, c.email, p.number, p.company, p.cancelReason, format(ann, 'dd/MM/yyyy')];
      }),
      `NEBB_alertas_comerciales_${todayStr}.csv`
    );
  };

  const TABS = [
    { id: 'expired',   label: `Vencidas sin renovar (${expired.length})`,     icon: AlertTriangle },
    { id: 'alerts',    label: `Alertas comerciales (${commercialAlerts.length})`, icon: RefreshCw },
    { id: 'cancelled', label: `Cancelaciones (${cancelled.length})`,           icon: XCircle },
    { id: 'expiring',  label: `Por vencer (${expiring.length})`,               icon: Clock },
  ];

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reportes</h1>
          <p className="page-subtitle">Análisis y exportación de datos</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--bg-card)', borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className="btn" style={{
            background: tab === t.id ? 'var(--bg-secondary)' : 'transparent',
            color: tab === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
            border: tab === t.id ? '1px solid var(--border)' : 'none',
            gap: 6,
          }}>
            <t.icon size={13} />{t.label}
          </button>
        ))}
      </div>

      {/* ── VENCIDAS SIN RENOVAR ── */}
      {tab === 'expired' && (
        <>
          <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--red)' }}>
            <strong>⚠️ {expired.length} pólizas vencidas</strong> — Revisá si ya fueron renovadas y actualizá manualmente el número y las fechas. Si no se renovaron, considerá contactar al cliente.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            {expired.length > 0 && <button className="btn btn-secondary btn-sm" onClick={exportExpired}><Download size={13} /> Exportar CSV</button>}
          </div>
          {expired.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              <AlertTriangle size={32} style={{ marginBottom: 12, opacity: 0.3 }} /><p>No hay pólizas vencidas sin renovar</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Cliente</th><th>Teléfono</th><th>N° Póliza</th><th>Compañía</th><th>Bien asegurado</th><th>Venció</th><th>Días vencida</th><th></th></tr>
                  </thead>
                  <tbody>
                    {expired.map(p => {
                      const c = getClient(p.clientId);
                      const asset = p.assetData || {};
                      const isVehicle = ['auto','moto','truck','fleet','boat'].includes(p.type);
                      const assetStr = isVehicle ? [asset.brand, asset.model, asset.plate].filter(Boolean).join(' · ') : asset.address || '';
                      const daysOverdue = p.endDate ? differenceInDays(today, parseISO(p.endDate)) : '—';
                      return (
                        <tr key={p.id}>
                          <td><div style={{ fontWeight: 500 }}>{c.name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div></td>
                          <td style={{ fontSize: 12 }}>{c.phone}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.number}</td>
                          <td>{p.company}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{assetStr}</td>
                          <td style={{ fontSize: 12, color: 'var(--red)' }}>{p.endDate ? fmtPatron(p.endDate, 'dd/MM/yyyy') : '—'}</td>
                          <td><span className="badge badge-red">{daysOverdue}d</span></td>
                          <td>
                            <button className="btn btn-amber btn-sm" onClick={() => navigate(`/policies/${p.id}`)}>
                              <RefreshCw size={11} /> Renovar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── ALERTAS COMERCIALES ── */}
      {tab === 'alerts' && (
        <>
          <div style={{ background: 'var(--accent-dim)', border: '1px solid rgba(124,108,248,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--accent)' }}>
            <strong>📞 Oportunidad comercial</strong> — Estas pólizas fueron canceladas por motivos recuperables (no venta ni siniestro total) y su aniversario de renovación cae en los próximos 30 días. El equipo comercial debería contactarlos para intentar recuperarlos.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            {commercialAlerts.length > 0 && <button className="btn btn-secondary btn-sm" onClick={exportAlerts}><Download size={13} /> Exportar CSV</button>}
          </div>
          {commercialAlerts.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              <RefreshCw size={32} style={{ marginBottom: 12, opacity: 0.3 }} /><p>No hay alertas comerciales en los próximos 30 días</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Cliente</th><th>Teléfono</th><th>N° Póliza</th><th>Compañía</th><th>Motivo cancelación</th><th>Aniversario renovación</th><th>Días restantes</th></tr>
                  </thead>
                  <tbody>
                    {commercialAlerts.map(p => {
                      const c = getClient(p.clientId);
                      const anniversary = addYears(parseISO(p.endDate || p.cancelDate), 1);
                      const daysLeft = differenceInDays(anniversary, today);
                      return (
                        <tr key={p.id}>
                          <td><div style={{ fontWeight: 500 }}>{c.name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div></td>
                          <td style={{ fontSize: 12 }}>{c.phone}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.number}</td>
                          <td>{p.company}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.cancelReason || '—'}</td>
                          <td style={{ fontSize: 12 }}>{format(anniversary, 'dd/MM/yyyy')}</td>
                          <td><span className={`badge ${daysLeft <= 7 ? 'badge-red' : daysLeft <= 15 ? 'badge-amber' : 'badge-blue'}`}>{daysLeft}d</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── CANCELACIONES ── */}
      {tab === 'cancelled' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{cancelled.length} pólizas canceladas · {cancelledRecoverable.length} con motivo recuperable</p>
            {cancelled.length > 0 && <button className="btn btn-secondary btn-sm" onClick={exportCancelled}><Download size={13} /> Exportar CSV</button>}
          </div>
          {cancelled.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              <XCircle size={32} style={{ marginBottom: 12, opacity: 0.3 }} /><p>No hay pólizas canceladas</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Cliente</th><th>Teléfono</th><th>N° Póliza</th><th>Compañía</th><th>Prima</th><th>Cancelada</th><th>Motivo</th><th>Recuperable</th></tr></thead>
                  <tbody>
                    {cancelled.map(p => {
                      const c = getClient(p.clientId);
                      const isRecoverable = !MOTIVOS_NO_RECUPERABLES.some(m => p.cancelReason?.toLowerCase().includes(m.toLowerCase()));
                      return (
                        <tr key={p.id}>
                          <td><div style={{ fontWeight: 500 }}>{c.name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div></td>
                          <td style={{ fontSize: 12 }}>{c.phone}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.number}</td>
                          <td>{p.company}</td>
                          <td style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>{p.premium ? '$' + Number(p.premium).toLocaleString('es-UY') : '—'}</td>
                          <td style={{ fontSize: 12 }}>{p.cancelDate ? fmtPatron(p.cancelDate, 'dd/MM/yyyy') : '—'}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180 }}>{p.cancelReason || '—'}</td>
                          <td>{isRecoverable ? <span className="badge badge-green">Sí</span> : <span className="badge badge-gray">No</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── POR VENCER ── */}
      {tab === 'expiring' && (
        <>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Pólizas que vencen en los próximos 60 días</p>
          {expiring.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              <Clock size={32} style={{ marginBottom: 12, opacity: 0.3 }} /><p>No hay vencimientos próximos</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Cliente</th><th>N° Póliza</th><th>Compañía</th><th>Bien asegurado</th><th>Vence</th><th>Días restantes</th><th></th></tr></thead>
                  <tbody>
                    {expiring.map(p => {
                      const c = getClient(p.clientId);
                      const days = differenceInDays(parseISO(p.endDate), today);
                      const asset = p.assetData || {};
                      const isVehicle = ['auto','moto','truck','fleet','boat'].includes(p.type);
                      const assetStr = isVehicle ? [asset.brand, asset.model, asset.plate].filter(Boolean).join(' · ') : asset.address || '';
                      return (
                        <tr key={p.id}>
                          <td><div style={{ fontWeight: 500 }}>{c.name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.phone}</div></td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.number}</td>
                          <td>{p.company}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{assetStr}</td>
                          <td style={{ fontSize: 12 }}>{fmtPatron(p.endDate, 'dd/MM/yyyy')}</td>
                          <td><span className={`badge ${days <= 7 ? 'badge-red' : days <= 15 ? 'badge-amber' : 'badge-blue'}`}>{days}d</span></td>
                          <td>
                            <button className="btn btn-amber btn-sm" onClick={() => navigate(`/policies/${p.id}`)}>
                              <RefreshCw size={11} /> Renovar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
